import React, { useEffect, useState } from "react";
import { Box, Badge, Flex, Text } from "@chakra-ui/react";
import { Eye, Pencil, EllipsisVertical } from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import {
    fetchCashoutsAction,
    updateCashoutAction,
} from "../../../../state/actions/cashoutActions";

import ReusableTable from "components/Tables/ReusableTable";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import ReusableDrawer from "components/Drawers/ReusableDrawer";

import ViewCashout from "./components/ViewCashout";
import EditCashoutDrawer from "./components/EditCashoutDrawer";

import { useCustomNotification } from "components/Toasts/OpenNotification";
import Skeleton from "react-loading-skeleton";

const CashoutManagement = () => {
    const dispatch = useDispatch();
    const notify = useCustomNotification();

    const { cashouts, loading, count } = useSelector((state) => state.cashOuts);

    const [selected, setSelected] = useState(null);
    const [viewOpen, setViewOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchCashoutsAction());
    }, [dispatch]);

    const tableData = cashouts || [];


    const handleUpdate = async (id, data) => {
        try {
            await dispatch(updateCashoutAction(id, data));
            setEditOpen(false);

            notify({
                type: "success",
                message: "Cashout Updated",
                description: `Status updated to ${data.status}`,
            });

            dispatch(fetchCashoutsAction());
        } catch (err) {
            notify({
                type: "error",
                message: "Error",
                description: "Failed to update cashout",
            });
        }
    };

    const statusColorScheme = {
        Approved: "green",
        Pending: "yellow",
        Suspended: "red",
        Rejected: "red",
        Deactivated: "gray",
    };

    const columns = [
        {
            Header: "User",
            accessor: (row) => `${row.first_name} ${row.last_name}`,
        },
        { Header: "Amount", accessor: "amount" },
        { Header: "Channel", accessor: "channel" },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ value }) => (
                <Badge
                    colorScheme={statusColorScheme[value] || "gray"}
                    borderRadius="full"
                    px={2}
                >
                    {value}
                </Badge>
            ),
        },
        {
            Header: "Claimed At",
            accessor: "claimed_at",
            Cell: ({ value }) => new Date(value).toLocaleString(),
        },

        {
            Header: "Actions",
            Cell: ({ row }) => (
                <ThreeDotMenu
                    icon={<EllipsisVertical size={16} />}
                    items={[
                        {
                            text: "View",
                            icon: <Eye size={16} />,
                            onClick: () => {
                                setSelected(row.original);
                                setViewOpen(true);
                            },
                        },
                        {
                            text: "Manage",
                            icon: <Pencil size={16} />,
                            onClick: () => {
                                setSelected(row.original);
                                setEditOpen(true);
                            },
                        },
                    ]}
                />
            ),
        },
    ];

    return (
        <Box>
            <Box p={6}>
                <Flex justifyContent="space-between" alignItems="center">
                    <Text fontSize="md" fontWeight="bold">
                        {loading ? (
                            <Skeleton height="20px" width="100px" />
                        ) : (
                            `All Requests: ${count ?? 0}`
                        )}
                    </Text>
                </Flex>
            </Box>
            <ReusableTable columnsData={columns} tableData={tableData} />

            <ReusableDrawer
                isOpen={viewOpen}
                onClose={() => setViewOpen(false)}
                headerContent={<h2>View Cashout</h2>}
                bodyContent={<ViewCashout cashout={selected} />}
            />
            <EditCashoutDrawer
                isOpen={editOpen}
                onClose={() => setEditOpen(false)}
                cashout={selected}
                onUpdate={handleUpdate}
            />
        </Box>
    );
};

export default CashoutManagement;